import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, STAGES } from '@/lib/mocks'
import { useCandidates } from '@/lib/candidateStore'
import { PageHeader, Avatar, MatchScore, SourceTag } from '@/lib/atsUi'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataList } from '@/components/ui/data-list'
import { Upload, FileSpreadsheet, Search, Check, X } from 'lucide-react'
import { toast } from 'sonner'
import type { Stage } from '@/lib/mocks'

export const Route = createFileRoute('/candidates/')({
  component: CandidatesPage,
})

const STAGE_BADGE_VARIANT: Record<Stage, 'success' | 'info' | 'warning' | 'error'> = {
  sourced: 'info',
  screened: 'info',
  l1: 'warning',
  l2: 'warning',
  offer: 'success',
  joined: 'success',
}

const STAGE_LABEL: Record<Stage, string> = {
  sourced: 'Sourced',
  screened: 'Screened',
  l1: 'Interview L1',
  l2: 'Interview L2',
  offer: 'Offer',
  joined: 'Joined',
}

const COLUMN_MAPPINGS = [
  { from: 'Name', to: 'name' },
  { from: 'Email', to: 'email' },
  { from: 'Phone', to: 'phone' },
  { from: 'Skills', to: 'skills' },
  { from: 'Current company', to: 'currentCompany' },
]

const SOURCES = ['All', 'Naukri', 'LinkedIn', 'Internal DB', 'Referral'] as const

function CandidatesPage() {
  const [showImport, setShowImport] = useState(false)
  const [query, setQuery] = useState('')
  const [stageFilter, setStageFilter] = useState<Stage | 'all'>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('All')
  const candidates = useCandidates()

  const filtered = candidates.filter((c) => {
    const q = query.toLowerCase()
    const matchesQuery =
      !q ||
      c.name.toLowerCase().includes(q) ||
      c.currentCompany.toLowerCase().includes(q) ||
      c.skills.some((s) => s.toLowerCase().includes(q))
    const matchesStage = stageFilter === 'all' || c.stage === stageFilter
    const matchesSource = sourceFilter === 'All' || c.source === sourceFilter
    return matchesQuery && matchesStage && matchesSource
  })

  function handleImport() {
    toast.success('Candidates imported')
    setShowImport(false)
  }

  return (
    <DashboardLayout title="Candidates" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Candidates"
        subtitle="Your whole candidate database — searchable in a click, ranked by AI."
        action={
          <Button size="sm" onClick={() => setShowImport((v) => !v)}>
            <Upload className="mr-1.5 h-3.5 w-3.5" />
            Import from Excel
          </Button>
        }
      />

      {/* Import panel */}
      {showImport && (
        <Card className="mb-6">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-sm font-medium text-muted-foreground">Import candidates</h2>
              <button
                type="button"
                onClick={() => setShowImport(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close import panel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/30 px-6 py-10 text-center mb-5">
              <FileSpreadsheet className="mb-3 h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium">Drag your Excel / CSV here, or browse</p>
              <p className="mt-1 text-xs text-muted-foreground">Supports .xlsx, .xls, .csv</p>
            </div>
            <div className="mb-5">
              <h3 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">Column mapping</h3>
              <div className="space-y-1.5">
                {COLUMN_MAPPINGS.map(({ from, to }) => (
                  <div key={from} className="flex items-center gap-2 text-sm">
                    <Check className="h-3.5 w-3.5 shrink-0 text-success" />
                    <span className="text-foreground">{from}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="font-mono text-xs text-muted-foreground">{to}</span>
                  </div>
                ))}
              </div>
            </div>
            <Button size="sm" onClick={handleImport}>
              Import candidates
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name, company, or skill…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search candidates"
            className="w-full rounded-md border border-input bg-card py-1.5 pl-8 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value as Stage | 'all')}
          aria-label="Filter by stage"
          className="rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All stages</option>
          {STAGES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          aria-label="Filter by source"
          className="rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {SOURCES.map((src) => (
            <option key={src} value={src}>
              {src === 'All' ? 'All sources' : src}
            </option>
          ))}
        </select>
      </div>

      {/* Paginated candidate list */}
      <DataList
        items={filtered}
        getKey={(c) => c.id}
        pageSize={5}
        empty="No candidates match your filters."
        renderRow={(c) => (
          <Link
            to="/candidates/$candidateId"
            params={{ candidateId: c.id }}
            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40"
          >
            <Avatar name={c.name} className="h-8 w-8 shrink-0 text-[11px]" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">{c.name}</span>
                <Badge variant={STAGE_BADGE_VARIANT[c.stage]} className="shrink-0">
                  {STAGE_LABEL[c.stage]}
                </Badge>
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {c.title} · {c.currentCompany}
              </div>
            </div>
            <div className="hidden max-w-[220px] flex-wrap gap-1 md:flex">
              {c.skills.slice(0, 3).map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground"
                >
                  {skill}
                </span>
              ))}
            </div>
            <div className="hidden w-24 shrink-0 text-xs text-muted-foreground lg:block">{c.location}</div>
            <div className="hidden shrink-0 sm:block">
              <SourceTag source={c.source} />
            </div>
            <div className="shrink-0">
              <MatchScore score={c.matchScore} />
            </div>
          </Link>
        )}
      />
    </DashboardLayout>
  )
}
